import { LOCAL_STORAGE_KEYS } from '@lit-protocol/constants'
import type { AuthSig } from '@lit-protocol/types'
import type { ICosmosSigner } from '@polaris/core/cosmos'
import type { IEvmSigner } from '@polaris/core/evm'
import { getMpcWalletAddressesQuery } from '@polaris/core/keys'
import {
  ChainType,
  makePartialChainTypeRecord,
  type SessionKeyData,
} from '@polaris/core/models'
import {
  LIT_ACTION_CIDS,
  newSessionExpirationDate,
  VAULT_SECRET_IPFS_HASHES,
} from '@polaris/core/mpc'
import type { INamadaSigner } from '@polaris/core/namada'
import {
  type AuthMethodAndScope,
  addAuthMethods,
  createMPCWallet,
  type GetPKPResponse,
  getPKP,
} from '@polaris/core/services'
import {
  generateMnemonicFromSignature,
  Hotkey,
  type WalletKeyring,
} from '@polaris/core/signing'
import type { ISolanaSigner } from '@polaris/core/solana'
import { featureFlagsAtom } from '@polaris/core/stores'
import {
  mpcTokenIdAtom,
  runnerSessionDataAtom,
} from '@polaris/core/stores/mpc/atom'
import { switchChain } from '@wagmi/core'
import { useAtom, useAtomValue } from 'jotai'
import { queryClientAtom } from 'jotai-tanstack-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addChain } from 'viem/actions'
import type { Config } from 'wagmi'
import { getConnectorClientQueryOptions } from 'wagmi/query'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { useCosmosWalletSigner } from '@/src/ecosystems/cosmos/use-wallet-signer'
import { wagmiConfig } from '@/src/ecosystems/evm/config'
import { useEvmWalletSigner } from '@/src/ecosystems/evm/use-wallet-signer'
import { useNamadaWalletSigner } from '@/src/ecosystems/namada/use-wallet-signer'
import { useSolanaWalletSigner } from '@/src/ecosystems/solana/use-wallet-signer'
import { useGuestModeSessionSigs } from '@/src/hooks/mpc/use-guest-mode-session-sigs'
import { useLitClient } from '@/src/hooks/mpc/use-lit-client'
import { usePkpScopedSessionSigs } from '@/src/hooks/mpc/use-pkp-scoped-session-sigs'
import { evmRegistryChainQuery } from '@/src/queries/keys/chains'
import { log } from '@/src/services/logger'
import { mpcWalletAddressesWithNamadaOverrideAtom } from '@/src/stores/mpc/atoms'
import { useLogout } from '@/src/stores/user/actions'
import { GUEST_USER, userAtom } from '@/src/stores/user/atoms'
import {
  SIWC_AUTH_METHOD_ID_PREFIX,
  SIWC_AUTH_METHOD_TYPE,
} from '@/src/utils/siwc'
import {
  SIWS_AUTH_METHOD_ID_PREFIX,
  SIWS_AUTH_METHOD_TYPE,
} from '@/src/utils/siws'
import { useKeyHierarchy } from '../mpc/use-key-hierarchy'
import { useRunnerSessionSigs } from '../mpc/use-runner-session-sigs'

// Logging of wallet addresses is handled by useLogWalletAddresses hook below

/**
 * Distillation of all signers into single keyring interface.
 *
 *
 * Note: The returned object is memoized and it's reference is stable across all
 * dependent wallet changes (e.g. wallet switch, chain switch, etc.).
 * This invariant must be maintained for all wallet signers.
 */
export function useWalletKeyring() {
  const cosmosSigner = useCosmosWalletSigner()
  const evmSigner = useEvmWalletSigner()
  const solanaSigner = useSolanaWalletSigner()
  const namadaSigner = useNamadaWalletSigner()
  const [runnerSessionData] = useAtom(runnerSessionDataAtom)
  const { data: mpcWalletAddresses } = useAtomValue(
    mpcWalletAddressesWithNamadaOverrideAtom,
  )
  const [mpcTokenId, setMpcTokenId] = useAtom(mpcTokenIdAtom)
  const { ensureKeyHierarchy } = useKeyHierarchy()
  const queryClient = useAtomValue(queryClientAtom)
  const { simpleAccountActivation } = useAtomValue(featureFlagsAtom)
  const { getPkpScopedSessionSigs } = usePkpScopedSessionSigs()
  const { litClient, connect: connectLitClient } = useLitClient()
  const { initiateGuestModeSessionSigs } = useGuestModeSessionSigs()
  const { runnerSessionKeyData } = useRunnerSessionSigs()
  const user = useAtomValue(userAtom)
  const logout = useLogout()
  // Request deduplication state
  const pendingRequests = useRef<Set<string>>(new Set())

  // Cleanup pending requests on unmount
  useEffect(() => {
    return () => {
      pendingRequests.current.clear()
    }
  }, [])

  /**
   * It allows to infer the signer type based on chainType enum
   */
  const getKeyringSigner = useMemo(() => {
    function getSigner(chainType: ChainType.Cosmos): ICosmosSigner
    function getSigner(chainType: ChainType.Evm): IEvmSigner
    function getSigner(chainType: ChainType.Solana): ISolanaSigner
    function getSigner(chainType: ChainType.Namada): INamadaSigner

    function getSigner(
      chainType: ChainType,
    ): ICosmosSigner | IEvmSigner | ISolanaSigner | INamadaSigner {
      switch (chainType) {
        case ChainType.Cosmos:
          return cosmosSigner
        case ChainType.Evm:
          return evmSigner
        case ChainType.Solana:
          return solanaSigner
        case ChainType.Namada:
          return namadaSigner
        case ChainType.Bitcoin:
          // https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
          throw new Error(
            '[useWalletKeyring.getSigner] Bitcoin wallet integration is suspended',
          )
        default:
          throw new Error(
            `[useWalletKeyring.getSigner] Unsupported chain type: ${chainType}`,
          )
      }
    }

    return getSigner
  }, [cosmosSigner, evmSigner, solanaSigner, namadaSigner])

  const getAddress = useCallback(
    (
      chainType: ChainType,
      chainId?: string,
    ): string | `0x${string}` | undefined => {
      switch (chainType) {
        case ChainType.Cosmos:
          return getKeyringSigner(chainType).deriveAddress(chainId)
        case ChainType.Evm:
          return getKeyringSigner(chainType).deriveAddress()
        case ChainType.Solana:
          return getKeyringSigner(chainType).deriveAddress()
        case ChainType.Bitcoin:
          // Bitcoin wallet integration is suspended
          // https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
          return undefined
        case ChainType.Namada:
          return mpcWalletAddresses?.namada
        default:
          throw new Error(
            `[useWalletKeyring.getAddress] Unsupported chain type: ${chainType}`,
          )
      }
    },
    [getKeyringSigner, mpcWalletAddresses],
  )

  const getPublicKey = useCallback(
    (chainType: ChainType): string | null => {
      switch (chainType) {
        case ChainType.Cosmos:
          return getKeyringSigner(chainType).getPublicKey()
        default:
          return null
      }
    },
    [getKeyringSigner],
  )

  /** Map from ChainType to connected address. */
  const addresses = useMemo(() => {
    const addrs = makePartialChainTypeRecord((chainType) =>
      getAddress(chainType),
    ) as Record<number, string>
    return addrs
  }, [getAddress])

  // Log addresses only when the set of values changes
  useLogWalletAddresses(addresses)

  const generateSignature = useCallback(
    async (chainType: ChainType, message: string) => {
      switch (chainType) {
        case ChainType.Cosmos: {
          const signer = getKeyringSigner(ChainType.Cosmos)
          return await signer.signMessage({ data: message })
        }

        case ChainType.Evm: {
          const id = 1

          const connector = await getQueryClient().ensureQueryData(
            getConnectorClientQueryOptions(wagmiConfig, {
              chainId: id as 1 | 42161 | 8453 | 10 | 137,
            }),
          )

          const { chain } = await getQueryClient().ensureQueryData(
            evmRegistryChainQuery({
              id: id.toString(),
            }),
          )

          /**
           * Try to switch to current chain, if it fails it could mean that chain is missing,
           * so we add it and then switch to it.
           */
          try {
            await switchChain<Config, number>(wagmiConfig, {
              chainId: id,
            })
          } catch {
            await addChain(connector, { chain })

            await switchChain<Config, number>(wagmiConfig, {
              chainId: id,
            })
          }

          const signer = getKeyringSigner(ChainType.Evm)
          const account = getAddress(ChainType.Evm) as `0x${string}`

          return await signer.signMessage({
            args: { account, message },
          })
        }
        case ChainType.Solana: {
          const signer = getKeyringSigner(ChainType.Solana)
          return await signer.signMessage({ message })
        }
        case ChainType.Bitcoin:
          throw new Error(
            '[useWalletKeyring.generateSignature] Bitcoin wallet integration is suspended', // https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
          )
        default:
          throw new Error(
            `[useWalletKeyring.generateSignature] Unsupported chain type: ${chainType}`,
          )
      }
    },
    [getAddress, getKeyringSigner],
  )

  const generateMnemonic = useCallback(
    async (
      chainType: ChainType,
      /**
       * We can specify a chain id if we need to switch the wallet, or if we don't specify it,
       * we'll just use the last connected chain
       */
      message: string = process.env.NEXT_PUBLIC_HOTKEYS_MESSAGE ??
        'Create Polaris Hotkey',
      /**
       * If true, uses cryptographically secure pseudo-randomness instead of deterministic signature-based generation
       * Defaults to the simpleAccountActivation feature flag value
       */
      noUserSignature: boolean = simpleAccountActivation,
    ): Promise<string> => {
      const strength = 128

      if (noUserSignature) {
        // Generate pseudo-random mnemonic using Web Crypto API
        // Prefer generateKey() for key generation as recommended by MDN for secure contexts
        const entropySize = strength / 8 // 16 bytes for 128-bit, 32 bytes for 256-bit

        let entropy: Uint8Array

        try {
          // Use AES-GCM as a secure entropy source by generating an AES key and extracting its raw bytes as randomness for mnemonic generation.
          // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/generateKey
          const key = await crypto.subtle.generateKey(
            // Reasons for AES-GCM:
            // - Secure context enforcement
            // - Perfect key size match for 128 bits of entropy
            // - Hardware acceleration on modern CPUs
            // - Extractable keys to export and use as entropy
            {
              name: 'AES-GCM',
              length: strength,
            },
            true, // extractable
            ['encrypt', 'decrypt'],
          )

          // Extract raw key material as entropy
          const keyBuffer = await crypto.subtle.exportKey('raw', key)
          entropy = new Uint8Array(keyBuffer).slice(0, entropySize)
        } catch (error) {
          // Fallback to getRandomValues() if generateKey() fails (e.g., insecure context possible)
          //E.g. generateKey fails if not on HTTPS.
          console.warn(
            'crypto.subtle.generateKey() failed, falling back to getRandomValues():',
            error,
          )
          // https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues
          entropy = new Uint8Array(entropySize)
          crypto.getRandomValues(entropy)
        }

        const { entropyToMnemonic } = await import('@scure/bip39')
        const { wordlist } = await import('@scure/bip39/wordlists/english')

        return entropyToMnemonic(entropy, wordlist)
      }
      // Fallback to deterministic signature-based generation
      const signature = await generateSignature(chainType, message)
      return generateMnemonicFromSignature(signature, strength)
    },
    [generateSignature, simpleAccountActivation],
  )

  const generateCosmosAuthMethods = useCallback(async () => {
    const { AUTH_METHOD_TYPE } = await import('@lit-protocol/constants')
    const address = getAddress(ChainType.Cosmos, 'osmosis-1')
    const pubkey = getPublicKey(ChainType.Cosmos)

    if (!address) {
      return undefined
    }

    const { keccak256, toBytes } = await import('viem')
    const { code: signInWithCosmos } = await import(
      '@polaris/lit-actions/src/generated/cosmos/signInWithCosmos'
    )

    if (!pubkey) {
      throw new Error('No public key found for Cosmos Signer')
    }

    const Hash = await import('typestub-ipfs-only-hash')

    const signInWithCodeWithTunnelUrl = signInWithCosmos

    const litActionCodeIpfsCid = await Hash.of(signInWithCodeWithTunnelUrl)

    const { LitContracts } = await import('@lit-protocol/contracts-sdk')

    // Step 3: Authenticate via Auth Lit Action
    const litActionAuthId = new LitContracts().utils.getBytesFromMultihash(
      litActionCodeIpfsCid,
    )

    const authMethod = {
      authMethodType: AUTH_METHOD_TYPE.LitAction,
      authMethodId: litActionAuthId,
    }

    const customAuthMethod = {
      authMethodType: keccak256(toBytes(SIWC_AUTH_METHOD_TYPE)),
      authMethodId: keccak256(
        toBytes(`${SIWC_AUTH_METHOD_ID_PREFIX}:${address}`),
      ),
    }

    return {
      authMethod,
      customAuthMethod,
    }
  }, [getAddress, getPublicKey])

  const generateSolanaAuthMethods = useCallback(async () => {
    const { AUTH_METHOD_TYPE } = await import('@lit-protocol/constants')
    const address = getAddress(ChainType.Solana)

    if (!address) {
      return undefined
    }

    const { keccak256, toBytes } = await import('viem')
    const { code: signInWithSolana } = await import(
      '@polaris/lit-actions/src/generated/solana/signInWithSolana'
    )

    const Hash = await import('typestub-ipfs-only-hash')

    const signInWithCodeWithTunnelUrl = signInWithSolana
    const litActionCodeIpfsCid = await Hash.of(signInWithCodeWithTunnelUrl)

    const { LitContracts } = await import('@lit-protocol/contracts-sdk')

    // Step 3: Authenticate via Auth Lit Action
    const litActionAuthId = new LitContracts().utils.getBytesFromMultihash(
      litActionCodeIpfsCid,
    )

    const authMethod = {
      authMethodType: AUTH_METHOD_TYPE.LitAction,
      authMethodId: litActionAuthId,
    }

    const customAuthMethod = {
      authMethodType: keccak256(toBytes(SIWS_AUTH_METHOD_TYPE)),
      authMethodId: keccak256(
        toBytes(`${SIWS_AUTH_METHOD_ID_PREFIX}:${address}`),
      ),
    }

    return {
      authMethod,
      customAuthMethod,
    }
  }, [getAddress])

  const generateEvmAuthMethods = useCallback(async () => {
    const address = getAddress(ChainType.Evm)

    if (!address) {
      return undefined
    }

    const { keccak256, toBytes } = await import('viem')
    const { AUTH_METHOD_TYPE } = await import('@lit-protocol/constants')

    const authMethod = {
      authMethodType: AUTH_METHOD_TYPE.EthWallet,
      authMethodId: keccak256(toBytes(`${address}:lit`)),
    }

    return {
      authMethod,
    }
  }, [getAddress])

  const _addAuthMethods = useCallback(
    async (tokenId: string, sessionKeyData: SessionKeyData) => {
      // Data validation - validate first to fail fast on invalid input
      if (!tokenId || typeof tokenId !== 'string' || tokenId.trim() === '') {
        log({
          message: 'Invalid tokenId provided to addAuthMethods',
          context: { tokenId },
          level: 'error',
        })
        throw new Error('Invalid tokenId: must be a non-empty string')
      }

      if (
        !sessionKeyData ||
        !sessionKeyData.walletSig ||
        !sessionKeyData.sessionKeyPair
      ) {
        log({
          message: 'Invalid sessionKeyData provided to addAuthMethods',
          context: {
            hasWalletSig: !!sessionKeyData?.walletSig,
            hasSessionKeyPair: !!sessionKeyData?.sessionKeyPair,
          },
          level: 'error',
        })
        throw new Error(
          'Invalid sessionKeyData: missing required walletSig or sessionKeyPair',
        )
      }

      // Request deduplication - after validation to ensure requestKey is based on valid data
      const requestKey = `${tokenId}-${JSON.stringify(sessionKeyData.walletSig?.address)}`
      if (pendingRequests.current.has(requestKey)) {
        log({
          message: 'Skipping duplicate addAuthMethods request',
          context: { requestKey, tokenId },
          level: 'info',
        })
        return
      }

      pendingRequests.current.add(requestKey)

      try {
        const { AUTH_METHOD_SCOPE } = await import('@lit-protocol/constants')

        const scopes = [
          AUTH_METHOD_SCOPE.PersonalSign,
          AUTH_METHOD_SCOPE.SignAnything,
        ]

        const authMethods: AuthMethodAndScope[] = []

        const cosmosAuthMethods = await generateCosmosAuthMethods()

        if (cosmosAuthMethods) {
          authMethods.push({
            authMethod: {
              authMethodType:
                cosmosAuthMethods.authMethod.authMethodType.toString(),
              id: cosmosAuthMethods.authMethod.authMethodId,
            },
            scopes,
            walletAddress: getAddress(ChainType.Cosmos),
          })

          authMethods.push({
            authMethod: {
              authMethodType: cosmosAuthMethods.customAuthMethod.authMethodType,
              id: cosmosAuthMethods.customAuthMethod.authMethodId,
            },
            scopes,
            walletAddress: getAddress(ChainType.Cosmos),
          })
        }

        const solanaAuthMethods = await generateSolanaAuthMethods()

        if (solanaAuthMethods) {
          authMethods.push({
            authMethod: {
              authMethodType:
                solanaAuthMethods.authMethod.authMethodType.toString(),
              id: solanaAuthMethods.authMethod.authMethodId,
            },
            scopes,
            walletAddress: getAddress(ChainType.Solana),
          })

          authMethods.push({
            authMethod: {
              authMethodType: solanaAuthMethods.customAuthMethod.authMethodType,
              id: solanaAuthMethods.customAuthMethod.authMethodId,
            },
            scopes,
            walletAddress: getAddress(ChainType.Solana),
          })
        }

        const evmAuthMethods = await generateEvmAuthMethods()

        if (evmAuthMethods) {
          authMethods.push({
            authMethod: {
              authMethodType:
                evmAuthMethods.authMethod.authMethodType.toString(),
              id: evmAuthMethods.authMethod.authMethodId,
            },
            scopes,
            walletAddress: getAddress(ChainType.Evm),
          })
        }

        // Validate we have valid auth methods before sending
        if (authMethods.length === 0) {
          log({
            message: 'No valid auth methods to add, skipping request',
            context: { tokenId },
            level: 'warn',
          })
          return
        }

        // Validate each auth method has required fields
        for (const authMethod of authMethods) {
          if (
            !authMethod.authMethod?.id ||
            !authMethod.authMethod?.authMethodType
          ) {
            log({
              message: 'Invalid auth method found, skipping request',
              context: {
                tokenId,
                authMethod: authMethod.authMethod,
                walletAddress: authMethod.walletAddress,
              },
              level: 'error',
            })
            throw new Error(
              'Invalid auth method: missing required id or authMethodType',
            )
          }
        }

        log({
          message: 'Adding MPC auth methods',
          context: {
            tokenId,
            authMethodsCount: authMethods.length,
          },
          level: 'info',
        })

        await addAuthMethods({
          tokenId,
          authMethods,
          sessionKeyData,
        })

        log({
          message: 'Successfully added MPC auth methods',
          context: { tokenId, authMethodsCount: authMethods.length },
          level: 'info',
        })
      } catch (error) {
        log({
          message: 'Failed to add MPC auth methods',
          context: {
            tokenId,
            requestKey,
            error: error instanceof Error ? error.message : String(error),
          },
          level: 'error',
          error: error instanceof Error ? error : undefined,
        })
        throw error
      } finally {
        pendingRequests.current.delete(requestKey)
      }
    },
    [
      generateCosmosAuthMethods,
      generateEvmAuthMethods,
      generateSolanaAuthMethods,
      getAddress,
    ],
  )

  const generateSessionSigs = useCallback(
    async (
      chainType?: ChainType,
    ): Promise<{
      sessionKeyData: SessionKeyData
    }> => {
      // Parallelize Lit client connection and imports for faster startup
      const { AUTH_METHOD_SCOPE } = await import('@lit-protocol/constants')

      const alreadyHasSessionSigs = !!runnerSessionKeyData
      // We keep google here for now as we had a bug in our backend that was not
      // updating the auth provider when the user was logged in with google.
      const isLoggedInWithMPCAuthProvider =
        user.auth_provider === 'google' || user.auth_provider === 'mpc'
      const isLoggedIn = user.id !== GUEST_USER.id
      const guestMode = !isLoggedIn || !isLoggedInWithMPCAuthProvider

      // If the user is logged in
      if (!alreadyHasSessionSigs && !guestMode) {
        await logout()
        window.location.reload()
        throw new Error(
          'Invalid MPC session, logging out, please try login again',
        )
      }

      if (alreadyHasSessionSigs) {
        log({
          message:
            'generateSessionSigs: Already has session sigs, skipping request',
          context: {
            sessionKeyData: !!runnerSessionKeyData,
            mpcTokenId: !!mpcTokenId,
          },
          level: 'info',
        })

        return {
          sessionKeyData: {
            sessionKeyPair: runnerSessionKeyData.sessionKeyPair,
            walletSig: runnerSessionKeyData.walletSig,
          } as SessionKeyData,
        }
      }

      const scopes = [
        AUTH_METHOD_SCOPE.PersonalSign,
        AUTH_METHOD_SCOPE.SignAnything,
      ]

      if (!chainType) {
        throw new Error('[generateSessionSigs]: chainType is undefined')
      }

      const sender = getAddress(chainType)
      let hotkey: Hotkey | undefined

      if (sender) {
        hotkey = Hotkey.restoreFromSender(sender)

        if (!hotkey) {
          const hotkeyMnemonic = await generateMnemonic(chainType)
          hotkey = Hotkey.fromMnemonics(sender, hotkeyMnemonic)
        }
      }

      if (!sender) {
        throw new Error('[generateSessionSigs]: sender is undefined')
      }

      if (!hotkey) {
        throw new Error('[generateSessionSigs]: hotkey is undefined')
      }

      const evmSigner = hotkey?.getSigner(ChainType.Evm)

      if (!evmSigner) {
        throw new Error('[generateSessionSigs]: evmSigner is undefined')
      }

      const { EthWalletProvider } = await import(
        '@lit-protocol/lit-auth-client'
      )

      const hotkeyEvmSender = hotkey.getAddress(ChainType.Evm)

      const expiration = newSessionExpirationDate()
      await connectLitClient()

      const authMethod = await EthWalletProvider.authenticate({
        signer: {
          async signMessage(message: string) {
            const signature = await evmSigner?.signMessage({
              args: {
                account: hotkeyEvmSender as `0x${string}`,
                message,
              },
            })

            return signature as string
          },
          getAddress() {
            return hotkeyEvmSender
          },
        },
        litNodeClient: litClient,
        expiration,
      })

      let response: GetPKPResponse

      // For EVM we need to determine the auth method ID from the access token
      const { getAuthIdByAuthMethod } = await import(
        '@lit-protocol/lit-auth-client'
      )
      const authMethodId = await getAuthIdByAuthMethod(authMethod)
      // Auth method without signature
      // Used to get and create PKPs
      const publicAuthMethod = {
        authMethodId,
        authMethodType: authMethod.authMethodType,
      }

      /**
       * If PKP doesn't exist we should create it
       */
      try {
        response = await getPKP({
          authMethod: publicAuthMethod,
        })
      } catch (e) {
        console.error(e)

        response = await createMPCWallet({
          authMethod: publicAuthMethod,
          scopes,
          walletCreatorAddress: sender,
        })
      }

      const tokenId = response.pkpInfo.tokenId

      const { sessionKey: sessionKeyPair, walletSig } =
        await initiateGuestModeSessionSigs(
          response.pkpInfo.publicKey,
          authMethod,
        )

      const sessionKeyData: SessionKeyData = {
        walletSig,
        sessionKeyPair,
      }

      // Only proceed if we have a valid tokenId
      if (!tokenId) {
        log({
          message: 'Cannot proceed without tokenId - session creation failed',
          context: { sessionKeyData: !!sessionKeyData },
          level: 'error',
        })
        throw new Error('Session creation failed: tokenId is undefined')
      }

      // Initialize key hierarchy for MPC privacy swaps (REQUIRED)
      // This creates root key, runner secret, and Namada key
      // Must succeed for privacy swaps to work - let errors propagate
      log({
        message: 'Initializing key hierarchy for MPC swaps',
        context: { tokenId },
        level: 'info',
      })

      const hierarchyResult = await ensureKeyHierarchy({
        // We use the runner session key data here as we need to generate the root key
        vaultSessionKeyData: sessionKeyData,
        guestMode: true,
      })

      log({
        message: 'Key hierarchy initialized successfully',
        context: {
          tokenId,
          rootKeyId: hierarchyResult?.rootKeyId,
          runnerSecretId: hierarchyResult?.runnerSecretId,
          namadaKeyId: hierarchyResult?.namadaKeyId,
        },
        level: 'info',
      })

      // Fetch wallet addresses (critical for UI)
      await queryClient.fetchQuery(
        getMpcWalletAddressesQuery({ sessionKeyData }),
      )

      // Add auth methods in background (non-blocking, for future recovery)
      // Backend now clears nonce tracker before executing to prevent nonce errors
      _addAuthMethods(tokenId, sessionKeyData).catch((error) => {
        log({
          message: 'Background addAuthMethods failed (non-critical)',
          context: {
            tokenId,
            error: error instanceof Error ? error.message : String(error),
          },
          level: 'warn',
          error: error instanceof Error ? error : undefined,
        })
      })

      setMpcTokenId(tokenId)

      return {
        sessionKeyData: sessionKeyData as SessionKeyData,
      }
    },
    [
      mpcTokenId,
      _addAuthMethods,
      setMpcTokenId,
      ensureKeyHierarchy,
      getAddress,
      generateMnemonic,
      queryClient,
      initiateGuestModeSessionSigs,
      logout,
      connectLitClient,
      litClient,
      user.id,
      user.auth_provider,
      runnerSessionKeyData,
    ],
  )

  const generateRunnerSessionSigs = useCallback(
    async (
      _chainType?: ChainType,
    ): Promise<{
      sessionKeyData: SessionKeyData
    }> => {
      await connectLitClient()

      const expiration =
        runnerSessionData?.expiration || newSessionExpirationDate()

      // Create scoped session sigs for runner operations
      // Include IPFS hashes for signing transactions, deriving keys, and Namada operations
      await getPkpScopedSessionSigs({
        ipfsHashes: [
          LIT_ACTION_CIDS.deriveWrappedKeyAndSignCosmosTransactionScoped,
          LIT_ACTION_CIDS.deriveWrappedKeyAndSignEvmTransactionScoped,
          LIT_ACTION_CIDS.deriveWrappedKeyAndSignSolanaTransactionScoped,
          LIT_ACTION_CIDS.deriveWrappedKeyAndSignHyperCoreTransactionScoped,
          LIT_ACTION_CIDS.deriveAndGetPubKeyK256Scoped,
          LIT_ACTION_CIDS.deriveAndGetPubKeyEd25519Scoped,

          // TODO: these are needed temporarily
          // https://linear.app/osmosis/issue/POL3-131/remove-temporary-scopes-from-runner-secret
          // End goal is to remove the derive wrapped key from runner secret
          // as we now use a "derive and sign" pattern.
          // Additionally, with vault operations being moved to FE,
          // we will not need vault scopes either.
          LIT_ACTION_CIDS.deriveWrappedKeyFromRunnerSecret,

          // Vault IPFS Hashes
          // TODO: remove this once we have the frontend implemented
          // https://linear.app/osmosis/issue/POL3-131/remove-temporary-scopes-from-runner-secret
          ...VAULT_SECRET_IPFS_HASHES,
        ],
        expiration,
      })

      const walletSig = JSON.parse(
        window.localStorage.getItem(LOCAL_STORAGE_KEYS.WALLET_SIGNATURE)!,
      ) as AuthSig

      const sessionKeyPair = litClient.getSessionKey()

      // Clear all Lit session data from localStorage
      // Lit may automatically set these. We do not want to persist runner session
      // sigs. By deleting these, we should regenerate the standard session sigs on-demand.
      window.localStorage.removeItem('lit-wallet-sig')
      window.localStorage.removeItem('lit-session-key')
      window.localStorage.removeItem('lit-session-expiration')

      const sessionKeyData: SessionKeyData = {
        walletSig,
        sessionKeyPair,
      }

      return {
        sessionKeyData,
      }
    },
    [
      runnerSessionData?.expiration,
      getPkpScopedSessionSigs,
      connectLitClient,
      litClient,
    ],
  )

  return useMemo(
    () =>
      ({
        addresses,
        getSigner: getKeyringSigner,
        getAddress,
        generateMnemonic,
        generateSessionSigs,
        generateRunnerSessionSigs,
      }) as WalletKeyring,
    [
      addresses,
      getKeyringSigner,
      getAddress,
      generateMnemonic,
      generateSessionSigs,
      generateRunnerSessionSigs,
    ],
  )
}

function useLogWalletAddresses(addresses: Record<number, string>) {
  const [lastLoggedKey, setLastLoggedKey] = useState<string | null>(null)

  useEffect(() => {
    const key = Object.values(addresses)
      .filter((addr) => Boolean(addr))
      .map((addr) => addr.toLowerCase())
      .sort()
      .join('|')

    if (key !== lastLoggedKey && process.env.NODE_ENV !== 'development') {
      console.info('Wallet addresses:', addresses)
      setLastLoggedKey(key)
    }
  }, [addresses, lastLoggedKey])
}
